import React, {useEffect, useState} from 'react';
import { Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CountryAnalyticDTO, CountryTopupAnalyticDTO, CountryUsageAnalyticDTO } from "../../../../dto/AnalyticDTO";

// Create a generic interface that accepts a data type parameter
interface CountryModalProps<T> {
    isVisible: boolean;
    onClose: () => void;
    title: string;
    data: T[];
    dataType: 'count' | 'usage' | 'topup';
}

// Create a type for any country data
type CountryData = CountryAnalyticDTO | CountryUsageAnalyticDTO | CountryTopupAnalyticDTO;

// Make the component generic
const CountryModal = <T extends CountryData>({
                                                 isVisible,
                                                 onClose,
                                                 title,
                                                 data,
                                                 dataType
                                             }: CountryModalProps<T>) => {

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10
    });

    // Reset pagination when modal opens or data changes
    useEffect(() => {
        if (isVisible) {
            setPagination({
                current: 1,
                pageSize: 10
            });
        }
    }, [isVisible, data]);

    // Handle pagination changes
    const handleTableChange = (pagination: any) => {
        setPagination({
            current: pagination.current,
            pageSize: pagination.pageSize
        });
    };

    const getTotal = (): number => {
        if (dataType === 'count') {
            return (data as CountryAnalyticDTO[]).reduce((acc, curr) => acc + curr.count, 0);
        } else if (dataType === 'usage') {
            return (data as CountryUsageAnalyticDTO[]).reduce((acc, curr) => acc + curr.totalConsumed, 0);
        } else {
            return (data as CountryTopupAnalyticDTO[]).reduce((acc, curr) => acc + curr.totalAmountSpent, 0);
        }
    };

    // Define type-specific columns with appropriate type assertions
    const getColumns = (): ColumnsType<T> => {
        const commonColumns = [
            {
                title: 'Country',
                dataIndex: ['country', 'name'],
                key: 'name',
                render: (text: string, record: T) => (
                    <div className="flex items-center">
                        <img
                            src={record.country.countryImageURL}
                            alt={record.country.name}
                            className="w-6 h-6 mr-3"
                        />
                        <span>{text}</span>
                    </div>
                ),
            },
        ];

        if (dataType === 'count') {
            return [
                ...commonColumns,
                {
                    title: 'Count',
                    dataIndex: 'count',
                    key: 'count',
                    sorter: (a: T, b: T) => {
                        // Type assertion is safe because we've verified dataType is 'count'
                        return (a as unknown as CountryAnalyticDTO).count - (b as unknown as CountryAnalyticDTO).count;
                    },
                },
            ] as ColumnsType<T>;
        } else if (dataType === 'usage') {
            return [
                ...commonColumns,
                {
                    title: 'Usage (GB)',
                    dataIndex: 'totalConsumed',
                    key: 'totalConsumed',
                    sorter: (a: T, b: T) => {
                        // Type assertion for usage data
                        return (a as unknown as CountryUsageAnalyticDTO).totalConsumed -
                            (b as unknown as CountryUsageAnalyticDTO).totalConsumed;
                    },
                    render: (value: number) => `${value} GB`,
                },
            ] as ColumnsType<T>;
        } else {
            return [
                ...commonColumns,
                {
                    title: 'Amount Spent ($)',
                    dataIndex: 'totalAmountSpent',
                    key: 'totalAmountSpent',
                    sorter: (a: T, b: T) => {
                        // Type assertion for topup data
                        return (a as unknown as CountryTopupAnalyticDTO).totalAmountSpent -
                            (b as unknown as CountryTopupAnalyticDTO).totalAmountSpent;
                    },
                    render: (value: number) => `$${value}`,
                },
            ] as ColumnsType<T>;
        }
    };

    // Format the total value based on data type
    const formatTotal = (): string | number => {
        const total = getTotal();
        if (dataType === 'count') {
            return total;
        } else if (dataType === 'usage') {
            return `${total} GB`;
        } else {
            return `$${total}`;
        }
    };

    return (
        <Modal
            title={title}
            open={isVisible}
            onCancel={onClose}
            footer={null}
            width={700}
        >
            <Table<T>
                dataSource={data}
                columns={getColumns()}
                rowKey={(record: T, index) => {
                    return record.country?.id ? record.country.id : `row-${index}`;
                }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                }}
                onChange={handleTableChange}
                summary={() => (
                    <Table.Summary fixed>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>
                                <strong>Total</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>
                                <strong>{formatTotal()}</strong>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
        </Modal>
    );
};

export default CountryModal;
